import React, { useEffect, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  LabelList,
} from 'recharts';
import './TradingSimDashboard.css';

// Polls the public, unauthenticated /api/display/state endpoint — no admin
// key involved, since this component is meant to render on a shared/
// projector screen where anyone can view page source.
const DISPLAY_ENDPOINT = '/api/display/state';
const POLL_INTERVAL_MS = 5000;
const NEWS_CYCLE_MS = 6000;
const REASONING_CYCLE_MS = 7000;
const TRANSITION_SLIDE_MS = 6000;

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatSignedCurrency(value) {
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatCurrency(Math.abs(value))}`;
}

function formatSignedPct(pct) {
  if (pct === null || pct === undefined) return '';
  const sign = pct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

// A single trade's reasoning, quoted, with the action it took — used
// underneath a winner/loser/mover slide so the room can see what the agent
// said going in next to what actually happened.
function ReasoningQuote({ who, trade }) {
  if (!trade) return null;
  return (
    <div className="transition-quote">
      <div className="transition-quote-action">
        {who ? `${who} — ` : ''}{trade.action.toUpperCase()} {trade.quantity} {trade.ticker || ''}
      </div>
      {trade.reasoning && <div className="transition-quote-text">&ldquo;{trade.reasoning}&rdquo;</div>}
    </div>
  );
}

// Builds the ordered list of slides for a round summary: winner, then loser
// (skipped if the same team swept both), then the biggest mover. Each slide
// carries whatever reasoning quotes are available so the overlay can show
// the decision right next to its outcome.
function buildTransitionSlides(summary) {
  if (!summary) return [];
  const { biggest_winner: winner, biggest_loser: loser, biggest_mover: mover } = summary;
  const sameTeam = winner && loser && winner.team === loser.team;
  const slides = [];

  if (winner) {
    slides.push({
      key: 'winner',
      icon: '🏆',
      label: 'Biggest Winner',
      team: winner.team,
      valueClass: 'delta-up',
      value: `${formatSignedCurrency(winner.delta)}${winner.pct !== null ? ` (${formatSignedPct(winner.pct)})` : ''}`,
      quotes: winner.trade ? [{ who: null, trade: winner.trade }] : [],
    });
  }
  if (loser && !sameTeam) {
    slides.push({
      key: 'loser',
      icon: '📉',
      label: 'Biggest Loser',
      team: loser.team,
      valueClass: 'delta-down',
      value: `${formatSignedCurrency(loser.delta)}${loser.pct !== null ? ` (${formatSignedPct(loser.pct)})` : ''}`,
      quotes: loser.trade ? [{ who: null, trade: loser.trade }] : [],
    });
  }
  if (mover) {
    slides.push({
      key: 'mover',
      icon: mover.direction === 'up' ? '🚀' : '🔥',
      label: 'Biggest Mover',
      team: `${mover.ticker} — ${mover.name}`,
      valueClass: mover.direction === 'up' ? 'delta-up' : 'delta-down',
      value: `${formatSignedPct(mover.pct_change)} this round`,
      quotes: (mover.trades || []).map((t) => ({ who: t.team, trade: t })),
    });
  }
  return slides;
}

function RoundTransitionOverlay({ summary, onDismiss }) {
  const slides = buildTransitionSlides(summary);
  const [index, setIndex] = useState(0);

  // Reset to the first slide every time a genuinely new summary comes in.
  useEffect(() => {
    setIndex(0);
  }, [summary]);

  useEffect(() => {
    if (slides.length === 0) return undefined;
    const isLast = index >= slides.length - 1;
    const id = setTimeout(() => {
      if (isLast) onDismiss();
      else setIndex((i) => i + 1);
    }, TRANSITION_SLIDE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[index];
  const isLast = index >= slides.length - 1;

  function handleClick() {
    if (isLast) onDismiss();
    else setIndex((i) => i + 1);
  }

  return (
    <div className="transition-overlay" onClick={handleClick}>
      <div className="transition-card">
        <div className="transition-label">
          Round Recap{slides.length > 1 ? ` · ${index + 1}/${slides.length}` : ''}
        </div>

        <div key={slide.key} className="transition-row transition-slide-in">
          <span className="transition-icon">{slide.icon}</span>
          <div>
            <div className="transition-sublabel">{slide.label}</div>
            <div className="transition-team">{slide.team}</div>
            <div className={`transition-value ${slide.valueClass}`}>{slide.value}</div>
          </div>
        </div>

        {slide.quotes.length === 0 && (
          <div className="transition-quote transition-quote-empty">No trade placed this round.</div>
        )}
        {slide.quotes.map((q, i) => (
          <ReasoningQuote key={i} who={q.who} trade={q.trade} />
        ))}

        {slides.length > 1 && (
          <div className="transition-dots">
            {slides.map((s, i) => (
              <span key={s.key} className={`transition-dot ${i === index ? 'active' : ''}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDelta(history) {
  if (!history || history.length < 2) return null;
  const latest = history[history.length - 1];
  const previous = history[history.length - 2];
  if (previous === 0) return null;
  const pct = ((latest - previous) / previous) * 100;
  return pct;
}

function CompanyCard({ company }) {
  const chartData = company.history.map((price, i) => ({ round: i, price }));
  const delta = formatDelta(company.history);
  const isUp = delta !== null && delta >= 0;

  return (
    <div className="company-card">
      <div className="company-card-header">
        <div>
          <div className="company-ticker">{company.ticker}</div>
          <div className="company-name">{company.name}</div>
        </div>
        <div className="company-price-block">
          <div className="company-price">{formatCurrency(company.price)}</div>
          {delta !== null && (
            <div className={`company-delta ${isUp ? 'delta-up' : 'delta-down'}`}>
              <span className="delta-icon">{isUp ? '▲' : '▼'}</span>
              {Math.abs(delta).toFixed(1)}%
            </div>
          )}
        </div>
      </div>
      <div className="company-sparkline">
        <ResponsiveContainer width="100%" height={64}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Tooltip
              contentStyle={{
                background: 'var(--surface-1)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={(round) => `Round ${round}`}
              formatter={(value) => [formatCurrency(value), 'Price']}
            />
            <Line
              type="monotone"
              dataKey="price"
              stroke="var(--series-1)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Leaderboard({ leaderboard }) {
  const data = leaderboard.map((entry) => ({
    name: entry.team,
    value: entry.total_value,
  }));
  const height = Math.max(200, data.length * 40);

  return (
    <div className="leaderboard-panel">
      <h2 className="panel-title">Leaderboard</h2>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 48, bottom: 4, left: 4 }}
        >
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={140}
            tick={{ fill: 'var(--text-secondary)', fontSize: 13 }}
            axisLine={{ stroke: 'var(--baseline)' }}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface-1)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
            }}
            formatter={(value) => [formatCurrency(value), 'Portfolio value']}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
            {data.map((entry, i) => (
              <Cell key={entry.name} fill={i === 0 ? 'var(--series-1-strong)' : 'var(--series-1)'} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              formatter={formatCurrency}
              fill="var(--text-primary)"
              fontSize={13}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function useCycler(items, intervalMs) {
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);

  useEffect(() => {
    if (!items || items.length === 0) return undefined;
    indexRef.current = 0;
    setIndex(0);
    const id = setInterval(() => {
      indexRef.current = (indexRef.current + 1) % items.length;
      setIndex(indexRef.current);
    }, intervalMs);
    return () => clearInterval(id);
  }, [items, intervalMs]);

  if (!items || items.length === 0) return null;
  return items[index % items.length];
}

function NewsTicker({ news }) {
  const items = news.map((n) => (n.ticker ? `[${n.ticker}] ${n.headline}` : n.headline));
  const current = useCycler(items, NEWS_CYCLE_MS);

  return (
    <div className="ticker news-ticker">
      <span className="ticker-label">NEWS</span>
      <span key={current} className="ticker-text">
        {current || 'Waiting for the next round…'}
      </span>
    </div>
  );
}

function ReasoningTicker({ trades }) {
  const items = trades.map(
    (t) => `${t.team} ${t.action.toUpperCase()} ${t.quantity} ${t.ticker} — "${t.reasoning}"`
  );
  const current = useCycler(items, REASONING_CYCLE_MS);

  return (
    <div className="ticker reasoning-ticker">
      <span className="ticker-label">AGENT THINKING</span>
      <span key={current} className="ticker-text">
        {current || 'No trades yet this round…'}
      </span>
    </div>
  );
}

export default function TradingSimDashboard() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [transition, setTransition] = useState(null);
  const initializedRef = useRef(false);
  const seenSummaryRoundRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(DISPLAY_ENDPOINT);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        setState(data);
        setError(null);

        // Detect a NEW round transition (not just "a summary exists" — on
        // first load we've missed whatever transition already happened, so
        // we silently note it as "seen" rather than replaying it late).
        const summary = data.last_round_summary;
        if (!initializedRef.current) {
          initializedRef.current = true;
          seenSummaryRoundRef.current = summary ? summary.round_id : null;
        } else if (summary && summary.round_id !== seenSummaryRoundRef.current) {
          seenSummaryRoundRef.current = summary.round_id;
          // No fixed timeout here anymore — RoundTransitionOverlay paces
          // itself through its own slides and calls dismissTransition when
          // it's done (or the viewer clicks through to the end).
          setTransition(summary);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  function dismissTransition() {
    setTransition(null);
  }

  if (error && !state) {
    return <div className="dashboard-root dashboard-error">Couldn't reach the market — {error}</div>;
  }
  if (!state) {
    return <div className="dashboard-root dashboard-loading">Loading market data…</div>;
  }

  return (
    <div className="dashboard-root">
      <header className="dashboard-header">
        <h1>Hackathon Trading Floor</h1>
        <div className="round-badge">
          {state.round_id ? `Round: ${state.round_id}` : 'No round open yet'}
        </div>
      </header>

      <div className="dashboard-body">
        <section className="companies-grid">
          {state.companies.map((company) => (
            <CompanyCard key={company.ticker} company={company} />
          ))}
        </section>

        <aside className="dashboard-sidebar">
          <Leaderboard leaderboard={state.leaderboard} />
        </aside>
      </div>

      <footer className="dashboard-footer">
        <NewsTicker news={state.news} />
        <ReasoningTicker trades={state.recent_trades} />
      </footer>

      <RoundTransitionOverlay summary={transition} onDismiss={dismissTransition} />
    </div>
  );
}